/**
 * seasons — Halloween and Christmas, from the holiday pack ("CRM design
 * overhaul request-2": OrbitGoogieV7 Halloween / Christmas, their Skies
 * boards, and the ChristmasSky / HalloweenSky scenes).
 *
 * SEASON-1. With Seasonal themes on (Settings → Appearance, on by
 * default), the season changes, the same on iOS (SeasonalTheme.swift):
 *
 *   • the sky: the season's painted scene for the time of day, animated
 *     (SeasonalSky.tsx), in place of the Googie gradient and its stars;
 *   • the ground and the accents: the boards' `seasonVars()` — ink,
 *     paper, washes, orange / teal / mustard — published as the shell's
 *     `--g-*` tokens (`seasonCssVars`);
 *   • Today: the quick actions in the season's colours, and the
 *     greeting in its own words for the last week, with the boards'
 *     offset echo behind it;
 *   • Nova's colours: pumpkin orange for Halloween, evergreen with a
 *     red light for Christmas (`NOVA_OUTFIT`);
 *   • the Aperture bar's light: embers, or evergreen and gold.
 *
 * Halloween runs all October and Christmas December 1–26 (Rennan). The
 * greeting turns festive October 25–31 and December 18–26.
 *
 * What a season never touches: colour that carries a meaning. A lead's
 * status, a project's stage, an expense category and a person's disc
 * keep their year-round hues (`G`), so "Pending" never turns into the
 * same amber as "Completed".
 */
import { G } from '@/lib/googie';

export type Season = 'halloween' | 'christmas';

export const SEASONS: Season[] = ['halloween', 'christmas'];

export const SEASON_LABEL: Record<Season, string> = {
  halloween: 'Halloween',
  christmas: 'Christmas',
};

export function isSeason(value: unknown): value is Season {
  return value === 'halloween' || value === 'christmas';
}

/** The season on `date`, if any: Halloween all October, Christmas
 *  December 1–26. Local time — the studio's own calendar. */
export function seasonOn(date: Date = new Date()): Season | null {
  const month = date.getMonth(); // 0-based
  if (month === 9) return 'halloween';
  if (month === 11 && date.getDate() <= 26) return 'christmas';
  return null;
}

/** The season's own words. */
export const SEASON_GREETING: Record<Season, string> = {
  halloween: 'Happy Halloween',
  christmas: 'Merry Christmas',
};

/** "Happy Halloween" October 25–31, "Merry Christmas" December 18–26. */
export function seasonGreeting(season: Season, date: Date = new Date()): string | null {
  const month = date.getMonth();
  const day = date.getDate();
  if (season === 'halloween') return month === 9 && day >= 25 ? SEASON_GREETING.halloween : null;
  return month === 11 && day >= 18 && day <= 26 ? SEASON_GREETING.christmas : null;
}

/** One season's colours — the boards' `seasonVars()`. */
export interface SeasonPalette {
  ink: string;
  paper: string;
  orange: string;
  teal: string;
  mustard: string;
  mustardLit: string;
  lime: string;
  cyan: string;
  /** The headline's offset echo (`--ink1`), drawn at 60%. */
  echo: string;
  /** The corner glow and the sky's ground while the painting loads. */
  glow: string;
  sky: string;
}

export const SEASON_PALETTE: Record<Season, SeasonPalette> = {
  halloween: {
    ink: '#F2E7D0',
    paper: '#12100E',
    // SEASON-SPREAD-1 — six accents, six hues. mustard, lime and cyan
    // were all #E8A227 and the rust teal was 1.6° off the pumpkin, so
    // six Today actions rendered as four oranges. Pumpkin, amber, dry
    // moss, pine and a phosphor ghost are all October, 22° apart at
    // the closest. Mirrored in GoogiePalette.halloween on iOS.
    orange: '#E8551F',      // pumpkin   hue  16
    teal: '#3F7A5C',        // pine      hue 150  (was rust #B9421C)
    mustard: '#E8A227',     // amber     hue  38
    mustardLit: '#F0B84E',
    lime: '#7D9A45',        // dry moss  hue  81  (was #E8A227)
    cyan: '#9FD6A8',        // ghost     hue 130  (was #E8A227)
    echo: '#E8551F',
    glow: '#E8551F',
    sky: 'linear-gradient(180deg,#1A0F0A 0%,#241109 42%,#2E1408 72%,#0E0806 100%)',
  },
  christmas: {
    ink: '#F0EAD6',
    paper: '#0B1512',
    // SEASON-SPREAD-1 — lime was the same evergreen as teal and the
    // mint 8° off it, so three slots were one green.
    orange: '#D8402F',      // red       hue   6
    teal: '#3E8C6A',        // evergreen hue 154
    mustard: '#CFAF62',     // gold      hue  42
    mustardLit: '#DDC283',
    lime: '#8CBF4A',        // holly     hue  86  (was #3E8C6A)
    cyan: '#A8456B',        // berry     hue 337  (was mint #8FC9A8)
    echo: '#D8402F',
    glow: '#D8402F',
    sky: 'linear-gradient(180deg,#0B1512 0%,#10201A 42%,#14261C 72%,#070C0A 100%)',
  },
};

/** `#RRGGBB` + alpha → `rgba()`. */
function rgba(hex: string, alpha: number): string {
  const n = hex.replace('#', '');
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * The app's own (shadcn) tokens in the season's colours, so a page
 * nobody has rebuilt yet — and every dialog — comes up in the season
 * too, the way googie.css repaints them for the Googie ground.
 */
const SEASON_APP_TOKENS: Record<Season, Record<string, string>> = {
  halloween: {
    '--background': '30 12% 6%',
    '--foreground': '41 57% 88%',
    '--card': '30 12% 10%',
    '--card-foreground': '41 57% 88%',
    '--popover': '30 12% 9%',
    '--popover-foreground': '41 57% 88%',
    '--primary': '38 81% 53%',
    '--primary-foreground': '30 12% 6%',
    '--secondary': '30 10% 15%',
    '--secondary-foreground': '41 57% 88%',
    '--muted': '30 10% 14%',
    '--muted-foreground': '41 22% 68%',
    '--accent': '15 74% 42%',
    '--accent-foreground': '41 57% 88%',
    '--border': '35 18% 20%',
    '--input': '30 10% 14%',
    '--ring': '38 81% 53%',
  },
  christmas: {
    '--background': '162 31% 6%',
    '--foreground': '46 46% 89%',
    '--card': '162 25% 10%',
    '--card-foreground': '46 46% 89%',
    '--popover': '162 25% 9%',
    '--popover-foreground': '46 46% 89%',
    '--primary': '42 53% 60%',
    '--primary-foreground': '162 31% 6%',
    '--secondary': '162 18% 15%',
    '--secondary-foreground': '46 46% 89%',
    '--muted': '162 18% 14%',
    '--muted-foreground': '46 18% 68%',
    '--accent': '154 39% 40%',
    '--accent-foreground': '46 46% 89%',
    '--border': '150 14% 20%',
    '--input': '162 18% 14%',
    '--ring': '42 53% 60%',
  },
};

/**
 * Every custom property a season publishes, for the shell and for
 * `<html>` (where Radix portals attach). The cards are darker glass than
 * the boards' 6% ivory wash: the painted skies are brighter than the
 * boards' plain gradient, and type has to read over them — the same
 * call iOS makes.
 */
export function seasonCssVars(season: Season): Record<string, string> {
  const p = SEASON_PALETTE[season];
  const card = season === 'christmas' ? 0.42 : 0.38;
  return {
    '--g-ink': p.ink,
    '--g-paper': p.paper,
    '--g-mute': rgba(p.ink, 0.62),
    '--g-card': rgba(p.paper, card),
    '--g-card2': rgba(p.paper, 0.6),
    '--g-line': rgba(p.ink, 0.18),
    '--g-line-strong': rgba(p.ink, 0.32),
    '--g-bar': rgba(p.paper, 0.72),
    '--g-fill': rgba(p.ink, 0.06),
    '--g-fill-2': rgba(p.ink, 0.12),
    '--g-track': rgba(p.ink, 0.14),
    '--g-on-ink': p.paper,
    '--g-orange': p.orange,
    '--g-teal': p.teal,
    '--g-mustard': p.mustard,
    '--g-mustard-lit': p.mustardLit,
    '--g-sky': p.sky,
    '--g-stars': '0',
    '--g-comet': '0',
    '--g-glow': p.glow,
    ...SEASON_APP_TOKENS[season],
  };
}

/** The decorative accents for `season` — Today's quick actions and
 *  stats. The year-round `G` when there's no season. */
export function seasonAccents(season: Season | null): {
  orange: string;
  teal: string;
  mustard: string;
  lime: string;
  cyan: string;
} {
  if (!season) return { orange: G.orange, teal: G.teal, mustard: G.mustard, lime: G.lime, cyan: G.cyan };
  const p = SEASON_PALETTE[season];
  return { orange: p.orange, teal: p.teal, mustard: p.mustard, lime: p.lime, cyan: p.cyan };
}

/** Nova's colours for the season (the boards' `--nv*`): her shell, its
 *  shadow side, and the light on her antenna. */
export interface NovaOutfit {
  body: string;
  bodyDark: string;
  antennaBall: string;
  /** The light inside the 3D Nova's arms (the 2D one has none). */
  glow: string;
}

export const NOVA_OUTFIT: Record<Season, NovaOutfit> = {
  // A pumpkin, with an amber light up top.
  halloween: { body: '#E8551F', bodyDark: '#A6350F', antennaBall: '#E8A227', glow: '#E8A227' },
  // Evergreen, with an ornament-red light up top.
  christmas: { body: '#3E8C6A', bodyDark: '#265943', antennaBall: '#D8402F', glow: '#8FC9A8' },
};

/** The Aperture bar's light in the season — embers for Halloween;
 *  evergreen, ornament red and gold for Christmas. Same stops as iOS. */
export const SEASON_AURA: Record<Season, string> = {
  halloween:
    'linear-gradient(100deg,#2a1206 0%,#7a2a0c 14%,#c2451a 28%,#e8551f 42%,#e8a227 56%,#c2451a 70%,#7a2a0c 84%,#2a1206 100%)',
  christmas:
    'linear-gradient(100deg,#0e2a1e 0%,#1e5a3e 14%,#3e8c6a 28%,#d8402f 42%,#cfaf62 56%,#3e8c6a 70%,#1e5a3e 84%,#0e2a1e 100%)',
};
